import { reactive, computed, toRefs } from 'vue'
import moment from 'moment'
import store from '@/store'
import { MessageBox } from 'element-ui'
import { isArray } from '@/common/js/utils'
import { Message } from 'view-design'
import { HOST_API } from '@/api/config'

import columns from '../../data/track_plan_column.js'
import {
  queryTracePlanByPage,
  uploadPtomData,
  imageBandingData,
  batchAddPlanWhiteList,
  batchRemovePlanWhiteList,
  cancelPlanImage,
  cancelPlanPage,
  batchCheckMaterial,
  batchUpdatePlanPage,
  modifyPlanAgentAccount,
  cancelProxyAccountPlan
} from '@/api/track.plan.model'

import { getPlanInfo } from '@/api/convert.model'

/**
 * @description: 追踪计划表格hook
 * @param {*} queryFormRef
 * @return {*}
 */
export const useTrackPlanTable = (queryFormRef) => {
  const tableModel = reactive({
    tableLoading: false,
    pager: {
      pageIndex: 1,
      pageSize: 40
    },
    total: 0,
    tableHeight: 500,
    tableList: [],
    showCustomTableColumnModal: false,
    tableColumns: [],
    rawTableCols: columns,

    selectList: []
  })

  const tableCols = computed(() => {
    return tableModel.tableColumns.filter((c) => c.checked)
  })

  function changePageIndex(index) {
    tableModel.pager.pageIndex = index
    getData()
  }
  function changePageSize(size) {
    tableModel.pager.pageSize = size
    changePageIndex(1)
  }

  function filterData() {
    tableModel.pager.pageIndex = 1
    getData()
  }

  function onTableColsChange(cols) {
    tableModel.showCustomTableColumnModal = false
    tableModel.tableColumns = cols
  }

  function setSelectList(val) {
    tableModel.selectList = val
  }

  // Methods
  function getData() {
    if (tableModel.tableLoading) return
    const params = {
      ...queryFormRef.value.buildParams(),
      ...tableModel.pager
    }
    tableModel.tableLoading = true
    queryTracePlanByPage(params)
      .then((data) => {
        tableModel.tableLoading = false
        if (!data) return
        const { list, page_info } = data
        if (isArray(list) && page_info) {
          const { total_number } = page_info
          tableModel.total = total_number || 0
          tableModel.tableList = list.map((ret) => {
            return {
              ...ret,
              optLoading: false,
              planMediaExt: ret.planMediaExt ? JSON.parse(ret.planMediaExt) : null
            }
          })
        } else tableModel.tableList = []
      })
      .finally(() => {
        tableModel.tableLoading = false
      })
  }

  const trackPlanTableActions = {
    /**
     * @description: 添加包体更新白名单
     * @return {*}
     */
    whiteListAdd() {
      const params = {
        ids: tableModel.selectList.map((ret) => ret.planId).join(',')
      }
      batchAddPlanWhiteList(params).then((data) => {
        tableModel.tableLoading = false
        filterData()
        if (data) Message.success('添加成功')
        else Message.error('添加失败')
      })
    },
    /**
     * @description: 移除包体更新白名单
     * @return {*}
     */
    whiteListDel() {
      const params = {
        ids: tableModel.selectList.map((ret) => ret.planId).join(',')
      }
      batchRemovePlanWhiteList(params).then((data) => {
        tableModel.tableLoading = false
        filterData()
        if (data) Message.success('移除成功')
        else Message.error('移除失败')
      })
    },
    /**
     * @description: 导出追踪计划
     * @return {*}
     */
    exportTrackPLan() {
      const planIds = tableModel.selectList.map((ret) => ret.planId).join(',')
      window.open(`${HOST_API}/api/ptom/tracePlan/exportplan2?planIds=${planIds}`)
      tableModel.tableLoading = false
    }
  }

  return {
    ...toRefs(tableModel),
    changePageIndex,
    changePageSize,
    filterData,
    getData,
    tableCols,
    setSelectList,
    onTableColsChange,
    trackPlanTableActions
  }
}

/**
 * @description: 页面权限
 * @return {*}
 */
export const usePageAccess = () => {
  const access = computed(() => store.state.accessAuth)
  const hasReportImport = computed(() => access.value.includes('M20989'))
  const hasTencentGameDataHelperPermit = computed(() => access.value.includes('M21033'))
  const hasImageBindImport = computed(() => access.value.includes('M20990'))
  const hasIOSTakeDownRemind = computed(() => access.value.includes('M20991'))
  const hasGiveAmountCalc = computed(() => access.value.includes('M20992'))
  const hasOperateWhiteList = computed(() => access.value.includes('M20993'))
  const hasOperateTrackPlan = computed(() => access.value.includes('M20994'))
  const hasPopularGameConfigPermit = computed(() => access.value.includes('M21065'))

  return {
    hasReportImport,
    hasTencentGameDataHelperPermit,
    hasImageBindImport,
    hasIOSTakeDownRemind,
    hasGiveAmountCalc,
    hasOperateWhiteList,
    hasOperateTrackPlan,
    hasPopularGameConfigPermit
  }
}

/**
 * @description: 计划详情
 * @return {*}
 */
export const usePlanDetailDrawer = () => {
  const detailDrawerModel = reactive({
    drawerInfo: {},
    showDetailsDrawer: false
  })

  const showDetailDrawerModal = async (row) => {
    try {
      if (!row.planId) throw new Error('planId reqiured')

      const { adId, advertiserId, creativeIds } = await getPlanInfo({ planId: row.planId })
      const info = {
        adId: adId || '',
        advertiserId: advertiserId || '',
        creativeIds: creativeIds || []
      }
      const today = moment().format('YYYY-MM-DD')
      detailDrawerModel.drawerInfo = Object.assign(
        {},
        row,
        {
          advertPosiName: row.advPositionName,
          productName: row.gameName,
          productId: row.gameId,
          adAccountName: row.mediaAccount,
          name: row.launchOpName,
          dateTime: [today, today]
        },
        info
      )
      detailDrawerModel.showDetailsDrawer = true
    } catch (e) {
      console.log('e output: ', e)
      Message.error('获取计划详情失败')
    }
  }

  return {
    ...toRefs(detailDrawerModel),
    showDetailDrawerModal
  }
}

/**
 * @description: 常用游戏管理
 * @return {*}
 */
export const useCommonlyGameConfig = () => {
  const configModel = reactive({
    showCommonlyGameConfigModal: false,
    commonlyGamesConfigSubmitLoading: false
  })

  const onCommonlyGamesConfigModalClose = () => {
    configModel.showCommonlyGameConfigModal = false
  }
  const onCommonlyGamesConfigConfirm = () => onCommonlyGamesConfigModalClose()
  return {
    ...toRefs(configModel),
    onCommonlyGamesConfigModalClose,
    onCommonlyGamesConfigConfirm
  }
}

/**
 * @description: 修改代投账号
 * @return {*}
 */
export const useAgentAccount = (selectList, getData) => {
  const agentModel = reactive({
    agentFormModal: false,
    agentForm: { accountId: '', msg: '', agentSubmitLoading: false, agentClearLaoding: false }
  })

  function selectAgent() {
    let proxyAccountIds = selectList.value.map((row) => row.proxyAccountId)
    proxyAccountIds = Array.from(new Set(proxyAccountIds))
    agentModel.agentFormModal = true

    if (proxyAccountIds.length === 1) {
      agentModel.agentForm.accountId = proxyAccountIds[0]
    }
  }

  const closeAgentFormModal = () => {
    agentModel.agentFormModal = false
    agentModel.agentForm.accountId = ''
    agentModel.agentForm.agentSubmitLoading = false
    agentModel.agentForm.agentClearLaoding = false
  }

  const onAgentModalConfirm = () => {
    const { accountId } = agentModel.agentForm

    const submitMethod = () => {
      agentModel.agentForm.agentSubmitLoading = true

      const params = { planIds: selectList.value.map((row) => row.planId) }
      if (accountId) {
        params.proxyAccountId = accountId
      }
      modifyPlanAgentAccount(params)
        .then((data) => {
          if (data) {
            agentModel.agentForm.agentSubmitLoading = false
            Message.success('修改成功')
            getData()
            closeAgentFormModal()
          }
        })
        .catch(() => {
          agentModel.agentForm.agentSubmitLoading = false
        })
    }

    if (!accountId) {
      MessageBox.confirm('本次操作会清空所选计划下的所有代投账号，是否继续？', '提示', { type: 'warning' }).then(() => {
        submitMethod()
      })
    } else {
      submitMethod()
    }
  }

  function clearAgentAccounts() {
    const planIds = selectList.value.map((row) => row.planId)
    MessageBox.confirm(`将清空${planIds.length}个计划的代投账号，确定吗？`, '提示', { type: 'warning' }).then(() => {
      agentModel.agentForm.agentClearLaoding = true
      cancelProxyAccountPlan({ list: planIds })
        .then((data) => {
          if (data) {
            Message.success('修改成功')
            getData()
            closeAgentFormModal()
          }
        })
        .finally(() => {
          agentModel.agentForm.agentClearLaoding = false
        })
    })
  }

  return {
    ...toRefs(agentModel),
    selectAgent,
    onAgentModalConfirm,
    closeAgentFormModal,
    clearAgentAccounts
  }
}

/**
 * @description: 文件导入导出
 * @return {*}
 */
export const useFilesAction = (filterData) => {
  const preUploadPtomData = (file) => {
    uploadPtomData({ file }).then((data) => {
      const { successTotal, failTotal } = data
      if (typeof successTotal === 'number' || typeof failTotal === 'number') {
        MessageBox.alert({
          title: '添加结果',
          message: `成功${data.successTotal}条，失败${data.failTotal}条,${data.message}`,
          closable: true
        })
        filterData()
      } else Message.error('导入数据失败！')
    })
    return false
  }

  const preImageBandingData = (file) => {
    imageBandingData({ file }).then((data) => {
      const { successTotal, failTotal } = data
      if (typeof successTotal === 'number' || typeof failTotal === 'number') {
        MessageBox.alert({
          title: '添加结果',
          content: `成功${data.successTotal}条，失败${data.failTotal}条`,
          closable: true
        })
        filterData()
      } else Message.error('导入数据失败！')
    })
    return false
  }

  const downImageBindTmp = () => {
    const url = `${HOST_API}/api/ptom/template/downTemplate?templateType=template_ptom_image_banding`
    window.open(url)
  }

  const downAmountEntryTmp = () => {
    const url = `${HOST_API}/api/ptom/template/downTemplate?templateType=template_ptom_batch_data`
    window.open(url)
  }

  return {
    preUploadPtomData,
    preImageBandingData,
    downImageBindTmp,
    downAmountEntryTmp
  }
}
